import React, { useEffect, useMemo, useState } from "react";
import { MdAccountTree, MdSync } from "react-icons/md";
import OrgChart from "./components/OrgChart";
import { OrgNode, UpdateOrgEmployeeRequest } from "../../../interfaces/Organograma";
import { getOrgTree, updateOrgEmployee, syncOrg } from "../../../services/orgApi";
import { usePermissions } from "../../../auth/PermissionProvider";
import { Permissions } from "../../../auth/permissions";

export default function OrganogramaPage() {
  const [nodes, setNodes] = useState<OrgNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState<{ type: "ok" | "err"; msg: string } | null>(null);

  // `org.manage.all` edita qualquer nó; `org.manage`, só a própria subárvore — a mesma divisão
  // que o OrganogramaController aplica, agora dita pela mesma chave dos dois lados.
  const { can } = usePermissions();
  const canEditAll = can(Permissions.OrgManageAll);
  const canEditOwnBranch = can(Permissions.OrgManage);
  const currentEmail = (localStorage.getItem("userEmail") ?? "").toLowerCase();

  const load = async () => {
    setLoading(true);
    try {
      setNodes(await getOrgTree());
    } catch {
      flash("err", "Falha ao carregar o organograma.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quem o usuário pode editar: `org.manage.all` = todos; `org.manage` = subárvore própria.
  const editableIds = useMemo(() => {
    const ids = new Set<number>();
    if (canEditAll) {
      nodes.forEach((n) => ids.add(n.id));
      return ids;
    }
    if (canEditOwnBranch && currentEmail) {
      const me = nodes.find((n) => (n.email ?? "").toLowerCase() === currentEmail);
      if (me) {
        const childrenOf = new Map<number, OrgNode[]>();
        nodes.forEach((n) => {
          if (n.managerEmployeeId != null) {
            const arr = childrenOf.get(n.managerEmployeeId) ?? [];
            arr.push(n);
            childrenOf.set(n.managerEmployeeId, arr);
          }
        });
        const stack = [me.id];
        while (stack.length) {
          const cur = stack.pop()!;
          (childrenOf.get(cur) ?? []).forEach((c) => {
            if (!ids.has(c.id)) {
              ids.add(c.id);
              stack.push(c.id);
            }
          });
        }
      }
    }
    return ids;
  }, [nodes, canEditAll, canEditOwnBranch, currentEmail]);

  const flash = (type: "ok" | "err", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2800);
  };

  const applyOptimistic = async (
    id: number,
    patch: Partial<OrgNode>,
    req: UpdateOrgEmployeeRequest
  ) => {
    const prev = nodes;
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, ...patch } : n)));
    try {
      await updateOrgEmployee(id, req);
    } catch (e: any) {
      setNodes(prev);
      flash("err", e?.response?.data?.message ?? "Não foi possível salvar a alteração.");
    }
  };

  // true se nodeId está na subárvore de ancestorId (sobe a cadeia de gestores).
  const isDescendant = (ancestorId: number, nodeId: number): boolean => {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    let cur: number | null | undefined = nodeId;
    let guard = 0;
    while (cur != null && guard++ < 10000) {
      if (cur === ancestorId) return true;
      cur = byId.get(cur)?.managerEmployeeId ?? null;
    }
    return false;
  };

  const onSetManager = (personId: number, managerId: number | null) => {
    if (managerId != null && (managerId === personId || isDescendant(personId, managerId))) {
      flash("err", "Isso criaria um ciclo (o gestor está abaixo dessa pessoa).");
      return;
    }
    applyOptimistic(
      personId,
      { managerEmployeeId: managerId },
      { setManager: true, managerEmployeeId: managerId }
    );
  };

  const onEditField = (personId: number, field: "jobDescription" | "department", value: string) =>
    applyOptimistic(
      personId,
      { [field]: value } as Partial<OrgNode>,
      { [field]: value } as UpdateOrgEmployeeRequest
    );

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await syncOrg();
      flash(
        "ok",
        `Sincronizado com o Entra: ${r.created} novos, ${r.linked} vinculados, ${r.updatedNames} nomes, ${r.deactivated} desativados (${r.totalEntra} no Entra).`
      );
      await load();
    } catch (e: any) {
      flash("err", e?.response?.data?.message ?? "Falha ao sincronizar com o Entra.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="mt-5 flex flex-col gap-5 animate-fade-up">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow">
            <MdAccountTree className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">Cadastros</p>
            <h1 className="text-2xl font-bold text-navy-700 dark:text-white">Organograma</h1>
          </div>
        </div>
        {canEditAll && (
          <button
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 rounded-lg bg-dv-gradient px-4 py-2 text-sm font-semibold text-white shadow-dv-glow disabled:opacity-60"
          >
            <MdSync className={syncing ? "h-5 w-5 animate-spin" : "h-5 w-5"} />
            {syncing ? "Sincronizando…" : "Sincronizar com Entra"}
          </button>
        )}
      </div>

      <p className="max-w-2xl text-sm text-gray-600 dark:text-gray-300">
        Arraste uma pessoa pelo <b>⠿</b> para cima do gestor (ou para “Topo da organização”) e edite
        cargo/área direto no card.{" "}
        {canEditAll
          ? "Você edita qualquer colaborador."
          : canEditOwnBranch
          ? "Você edita apenas o seu time; os demais ficam em modo leitura."
          : "Você tem acesso somente leitura."}
      </p>

      {toast && (
        <div
          className={`rounded-lg px-4 py-2 text-sm ${
            toast.type === "ok" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}
        >
          {toast.msg}
        </div>
      )}

      {loading ? (
        <div className="animate-pulse rounded-2xl border border-gray-200 p-10 text-center text-gray-400 dark:border-navy-700">
          Carregando organograma…
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
          <OrgChart
            nodes={nodes}
            editableIds={editableIds}
            onSetManager={onSetManager}
            onEditField={onEditField}
          />
        </div>
      )}
    </div>
  );
}
